'use client';

import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';

import { Switch } from '../../primitives/switch';
import { cn } from '../../utils/cn';

type SwitchBase = ComponentPropsWithoutRef<typeof Switch>;

export interface UzSwitchProps extends SwitchBase {
  label?: ReactNode;
  description?: ReactNode;
  /** Position of label/description relative to the switch. */
  labelPosition?: 'right' | 'left';
  containerClassName?: string;
}

export const UzSwitch = forwardRef<ElementRef<typeof Switch>, UzSwitchProps>(
  (
    {
      label,
      description,
      labelPosition = 'right',
      containerClassName,
      id: providedId,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = providedId ?? autoId;

    const labelBlock = (label || description) && (
      <div className={cn('grid gap-0.5', labelPosition === 'left' && 'text-right')}>
        {label && (
          <label
            htmlFor={id}
            className={cn('text-sm leading-none font-medium', disabled && 'opacity-60')}
          >
            {label}
          </label>
        )}
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
      </div>
    );

    return (
      <div className={cn('flex items-start gap-3', containerClassName)}>
        {labelPosition === 'left' && labelBlock}
        <Switch id={id} ref={ref} disabled={disabled} {...rest} />
        {labelPosition === 'right' && labelBlock}
      </div>
    );
  },
);
UzSwitch.displayName = 'UzSwitch';
